/*
__/\\\\\\\\\\\\\\\__/\\\\\\\\\\\\\\\_____/\\\\\\\\\____        
 _\///////\\\/////__\///////\\\/////____/\\\\\\\\\\\\\__       
  _______\/\\\_____________\/\\\________/\\\/////////\\\_      
   _______\/\\\_____________\/\\\_______\/\\\_______\/\\\_     
    _______\/\\\_____________\/\\\_______\/\\\\\\\\\\\\\\\_    
     _______\/\\\_____________\/\\\_______\/\\\/////////\\\_   
      _______\/\\\_____________\/\\\_______\/\\\_______\/\\\_  
       _______\/\\\_____________\/\\\_______\/\\\_______\/\\\_ 
        _______\///______________\///________\///________\///__
            
            COPYRIGHT TACTICAL TRANSPORTATION ADVISORS, INC. 
            ALL RIGHTS RESERVED.
*/

import Address from "../../../../models/Address";
import StateObject from "../../../../models/State/StateObject";
import { addressToString, validateDecimal, validateInteger } from "../../../../tools";

export default class W4 extends StateObject {
  documentType;
  firstNameMiddleInitial;
  lastName;
  ssn;
  address;
  cityStateZip;
  filingStatus;
  has2Jobs;
  step3Children;
  step3OtherDependents;
  step3Total;
  step4OtherIncome;
  step4Deductions;
  step4ExtraWitholding;
  claimExemption;
  step5Signed;
  employerName;
  employerAddress;
  firstDateOfEmployment;
  ein;
  completed = false;

  constructor(
    firstNameMiddleInitial,
    lastName,
    ssn,
    address,
    cityStateZip,
    filingStatus,
    has2Jobs,
    step3Children,
    step3OtherDependents,
    step3Total,
    step4OtherIncome,
    step4Deductions,
    step4ExtraWitholding,
    claimExemption,
    step5Signed,
    employerName,
    employerAddress,
    firstDateOfEmployment,
    ein
  ) {
    super();
    this.firstNameMiddleInitial = firstNameMiddleInitial;
    this.lastName = lastName;
    this.ssn = ssn;
    this.address = address;
    this.cityStateZip = cityStateZip;
    this.filingStatus = filingStatus;
    this.has2Jobs = has2Jobs;
    this.step3Children = step3Children;
    this.step3OtherDependents = step3OtherDependents;
    this.step3Total = step3Total;
    this.step4OtherIncome = step4OtherIncome;
    this.step4Deductions = step4Deductions;
    this.step4ExtraWitholding = step4ExtraWitholding;
    this.claimExemption = claimExemption;
    this.step5Signed = step5Signed;
    this.employerName = employerName;
    this.employerAddress = employerAddress;
    this.firstDateOfEmployment = firstDateOfEmployment;
    this.ein = ein;
    this.initChildren();
  }

  static initDefault() {
    return new W4("", "", "", "", "", "", undefined, false, "", "", "", "", "", "", false, false, "", "", "", "", "", "", "");
  }

  static initFromData(document, userData, companyData, startDate) {
    const firstName = userData.firstName ?? "";
    const middleInitial = userData.middleName?.[0] ?? "";
    const lastName = userData.lastName ?? "";
    const address = Address.decodeNonStrict(userData.address);

    return new W4(
      document.firstNameMiddleInitial ?? `${firstName} ${middleInitial}`,
      document.lastName ?? lastName,
      document.ssn ?? "",
      document.address ?? `${address?.thoroughfare} ${address?.premise}`,
      document.cityStateZip ?? `${address?.locality}, ${address?.administrativeArea} ${address?.postalCode}`,
      document.filingStatus ?? undefined,
      document.has2Jobs ?? false,
      document.step3Children ?? "",
      document.step3OtherDependents ?? "",
      document.step3Total ?? "",
      document.step4OtherIncome ?? "",
      document.step4Deductions ?? "",
      document.step4ExtraWitholding ?? "",
      document.claimExemption ?? false,
      false,
      companyData.name ?? companyData.companyName ?? "",
      companyData.address ? addressToString(companyData.address) : "",
      startDate ?? "",
      companyData.ein ?? ""
    );
  }

  encode(document, base64) {
    return {
      ...document,
      base64: base64,
      firstNameMiddleInitial: this.firstNameMiddleInitial,
      lastName: this.lastName,
      ssn: this.ssn,
      address: this.address,
      cityStateZip: this.cityStateZip,
      filingStatus: this.filingStatus,
      has2Jobs: this.has2Jobs,
      step3Children: validateInteger(this.step3Children),
      step3OtherDependents: validateInteger(this.step3OtherDependents),
      step3Total: validateDecimal(this.step3Total),
      step4OtherIncome: this.step4OtherIncome ? validateDecimal(this.step4OtherIncome) : null,
      step4Deductions: this.step4Deductions ? validateDecimal(this.step4Deductions) : null,
      step4ExtraWitholding: this.step4ExtraWitholding ? validateDecimal(this.step4ExtraWitholding) : null,
      claimExemption: this.claimExemption,
      step5Signed: this.step5Signed,
    };
  }
}
